import React, { useCallback, useEffect, useMemo } from 'react';
import { ContentHeader, NormalButton } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';

import { useOnceReveal } from '@common/hooks';
import { PdpProductSizeType, showToast, useIsAuthenticated } from '@domains/pdp-common';

import { useGetUserAccountBodyQuery } from '../../size-info/queries/useGetUserAccountBodyQuery';
import { useSizeInfoAgreementListener } from '../../size-info/services/useSizeInfoAgreementListener';
import { useGetUserAccountProductMeasurementListQuery } from '../../size-info/services/useSizeInfoMutation';
import { useAgreementPopup } from '../../size-info/utils/agreement-popup';
import { useSizeRecommendationUBL } from '../log/useSizeRecommendationUBL';
import { useSizeRecommendationService } from '../services/useSizeRecommendationService';
import { clearOldSessionStorage, handleImpressionLog } from '../utils/sessionHelper';
import { OthersSizeContainer } from './OthersSizeContainer';
import { SimilarSizeContainer } from './SimilarSizeContainer';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const UserBodyInput = dynamic(() => import('zigzag-review/UserBodyInput'), {
  ssr: false,
}) as any;
export interface SizeRecommendationSectionContainerProps {
  onSizeRecommendRefChange?: (node: HTMLDivElement) => void;
  disabledGutter?: boolean;
}

export const SizeRecommendationSectionContainer: React.FC<
  React.PropsWithChildren<SizeRecommendationSectionContainerProps>
> = ({ onSizeRecommendRefChange, disabledGutter }) => {
  const { sizeRecommendationImpression, inputBodyInfoImpressionUBL, saveBodyInfoClickUBL } = useSizeRecommendationUBL();
  const { targetRef, isRevealed } = useOnceReveal();
  // 훅을 한 번만 호출하여 상태 공유 (타이밍 이슈 방지)
  const {
    catalogProductId,
    visible,
    isLoadingIntegratedData,
    sizeRecommendation,
    recommendationType,
    userAccount,
    title,
    components,
    handleChange,
    submit,
  } = useSizeRecommendationService();

  // 동의 바텀시트 관련
  const { isLoggedIn: isAuthenticated } = useIsAuthenticated();
  const { data: userAccountBodyData } = useGetUserAccountBodyQuery();
  const hasBody = userAccountBodyData?.user_account?.has_body ?? false;
  const { data: userMeasurementData } = useGetUserAccountProductMeasurementListQuery({ enabled: isAuthenticated });
  const userMeasurementList = userMeasurementData?.data?.user_account_product_measurement_list?.item_list;
  const isUserSizeAgreed =
    userMeasurementData?.data?.user_account_product_measurement_list?.is_size_info_agreed ?? false;
  const { openSizeRecommendationPopup } = useAgreementPopup(catalogProductId);

  useSizeInfoAgreementListener({
    onAgree: (payload) => {
      if (payload.type !== 'recommend') return;
      submit();
    },
    onSelectMyPurchase: () => {},
    onCompareDirectInput: () => {},
  });

  // 체형 정보 저장 버튼 클릭 핸들러 (로그 포함)
  const handleSubmit = useCallback(() => {
    const hasExistingBody = Boolean(userAccount?.body);
    const savedOptionList = components
      .filter(
        (component): component is Extract<typeof component, { __typename: 'UxUserBodySelectionInputInfo' }> =>
          component.__typename === 'UxUserBodySelectionInputInfo',
      )
      .map((component) => ({
        name: component.key || '',
        value_list: component.selected_value ? [String(component.selected_value)] : [],
      }));

    saveBodyInfoClickUBL({
      is_body_info_exist: hasExistingBody,
      saved_option_list: savedOptionList,
    });

    // 로그인 상태이고 체형+피부+사이즈 정보가 모두 없으면 동의 바텀시트 먼저 노출
    // hasBody: 체형+피부 정보, isUserSizeAgreed: 사이즈 정보
    if (isAuthenticated && !hasBody && !isUserSizeAgreed) {
      // 입력값 검증
      const inputComponents = components.filter(
        (component): component is Extract<typeof component, { __typename: 'UxUserBodySelectionInputInfo' }> =>
          component.__typename === 'UxUserBodySelectionInputInfo',
      );
      const isAllSelected =
        inputComponents.length > 0 &&
        inputComponents.every((component) => {
          return component.selected_value !== undefined && component.selected_value !== null;
        });

      if (!isAllSelected) {
        showToast('체형을 선택해 주세요');
        return;
      }

      openSizeRecommendationPopup();
      return;
    }

    submit();
  }, [
    userAccount,
    components,
    saveBodyInfoClickUBL,
    submit,
    isAuthenticated,
    hasBody,
    isUserSizeAgreed,
    openSizeRecommendationPopup,
  ]);

  useEffect(() => {
    // 오래된 세션 스토리지 데이터 정리
    clearOldSessionStorage(catalogProductId);

    if (visible && isRevealed) {
      handleImpressionLog({
        catalogProductId,
        sizeRecommendation,
        sizeRecommendationImpression,
      });

      // 체형 정보 미입력 상태일 때 입력 폼 노출 로그
      if (recommendationType === PdpProductSizeType.NONE || !userAccount || !userAccount.body) {
        inputBodyInfoImpressionUBL();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isRevealed]);

  const Content = useMemo(() => {
    // [미노출] 로딩 중이거나 size_recommendation이 null이면 영역 미노출
    if (isLoadingIntegratedData || !visible) {
      return null;
    }

    const isNoneType = recommendationType === PdpProductSizeType.NONE;
    // [체형정보 미입력] 의류: 키, 몸무게 둘 중 하나라도 없을 경우 / 신발: 신발 사이즈 없을 경우
    if (!userAccount || !userAccount.body) {
      return (
        <UnregisteredSizeContainer>
          <ContentHeader title='사이즈 추천' />
          <UnregisteredSizeContent>
            <UserBodyInput
              title={title}
              components={components}
              dropdownHeight={330}
              loading={false}
              onChange={(key: string, value: string) => handleChange(key, value)}
              renderSubmitButton={() => (
                <NormalButton size='small' onClick={handleSubmit} fullWidth>
                  선택 완료
                </NormalButton>
              )}
            />
          </UnregisteredSizeContent>
        </UnregisteredSizeContainer>
      );
    }

    // [엠티뷰 또는 옵션별 추천] 체형 정보는 있지만 추천 데이터가 없거나 옵션별 추천인 경우
    if (isNoneType || recommendationType === PdpProductSizeType.OPTION) {
      return <OthersSizeContainer />;
    }

    // [유사 체형 추천]
    return <SimilarSizeContainer />;
  }, [
    visible,
    isLoadingIntegratedData,
    recommendationType,
    userAccount,
    title,
    components,
    handleChange,
    handleSubmit,
  ]);

  return (
    <div ref={targetRef}>
      {Content ? (
        <Layout ref={onSizeRecommendRefChange} disabledGutter={disabledGutter}>
          {Content}
        </Layout>
      ) : null}
    </div>
  );
};

const UnregisteredSizeContainer = styled.div`
  background: ${vars.color.backgroundSurface};
`;

const UnregisteredSizeContent = styled.div`
  padding: 0 16px 24px 16px;
`;

const Layout = styled.div<{ disabledGutter?: boolean }>`
  margin-bottom: 8px;
`;
